import { useTranslation } from 'react-i18next';
import { Confirmation } from '@otp/ui';
import {
  EditRoleDocument,
  RolesDocument,
  RoleUsagesDocument,
  RoleUsagesQuery,
  RoleUsagesType,
  User,
} from 'src/graphql/generated/graphql';
import React from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { RoleListItem } from 'src/components/roles/list/types';
import { TFunction } from 'i18next';
import { UsageRelation } from 'src/components/common/modals/deleting-used-item-modal';
import { Routes } from 'src/components/common/router/constants';
import { RoleAction } from 'src/components/roles/list/table/columns/actions/types';
import { DeleteConfirmationCheckModal } from 'src/components/common/modals/delete-confirmation-check';

const prepareRoleUsageData = (
  roleUsages: RoleUsagesQuery['roleUsages'],
  { t }: { t: TFunction },
): UsageRelation[] => {
  const usageMap = {
    [RoleUsagesType.RoleInheritedByOtherRoles]: {
      name: t('common.roles'),
      icon: null,
      getLink: (id: string) => `${Routes.roles.path}/${id}`,
    },
    [RoleUsagesType.RoleUsedByLoginRules]: {
      name: t('common.login-rules'),
      icon: null,
      getLink: (id: string) => `${Routes.loginRules.path}/${id}`,
    },
    [RoleUsagesType.RoleUsedByUserGroups]: {
      name: t('common.user-groups'),
      icon: null,
      getLink: (id: string) => `${Routes.userGroups.path}/${id}`,
    },
    [RoleUsagesType.RoleUsedByUsers]: {
      name: t('common.users'),
      icon: null,
      getLink: (id: string) => `${Routes.users.path}/${id}`,
    },
  };

  return (roleUsages || []).map((usage) => {
    const usageMapItem = usageMap[usage.code];

    return {
      name: usageMapItem.name,
      icon: usageMapItem.icon,
      items: (usage.data || [])?.map((item) => {
        const name =
          usage.code === RoleUsagesType.RoleUsedByUsers
            ? `${(item as User).firstName} ${(item as User).lastName}`
            : (item as { name: string }).name;
        return {
          id: item.id,
          name,
          link: usageMapItem.getLink(item.id),
        };
      }),
    };
  });
};

export const DeleteModals = ({
  action,
  onClose,
  role,
  onFinished,
}: {
  action: RoleAction;
  onClose: () => void;
  onSubmitted?: (action: RoleAction) => void;
  role: RoleListItem;
  onFinished: () => void;
}) => {
  const { t } = useTranslation();

  const [updateRole] = useMutation(EditRoleDocument, {
    refetchQueries: [RolesDocument],
  });

  const deleteRole = () =>
    updateRole({
      variables: {
        id: role.id,
        data: {
          isDeleted: true,
        },
      },
    });

  const disableRole = () =>
    updateRole({
      variables: {
        id: role.id,
        data: {
          isDisabled: true,
        },
      },
    });

  const enableRole = () =>
    updateRole({
      variables: {
        id: role.id,
        data: {
          isDisabled: false,
        },
      },
    });

  const [getRoleUsages] = useLazyQuery(RoleUsagesDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const onDeleteRoleToggle = async (toDelete: boolean) => {
    if (toDelete) {
      try {
        await deleteRole();
      } catch (e: any) {
        const error = e?.networkError?.result;

        // ToDo: add snack bar for this error
        console.log({ error });
      }
    }

    onFinished();
  };

  const onDisableRoleToggle = async (toDisable: boolean) => {
    if (toDisable) {
      try {
        await disableRole();
      } catch (e: any) {
        const error = e?.networkError?.result;

        // ToDo: add snack bar for this error
        console.log({ error });
      }
    } else {
      await enableRole();
    }

    onFinished();
  };

  const checkRoleUsageBeforeDelete = async () => {
    const { data } = await getRoleUsages({
      variables: { id: role.id },
    });
    const roleUsages = data?.roleUsages;
    if (roleUsages?.length) {
      const preparedRoleUsageData = prepareRoleUsageData(roleUsages, {
        t,
      });
      if (preparedRoleUsageData?.length) {
        return preparedRoleUsageData;
      }
    }

    return undefined;
  };

  return (
    <>
      {action === RoleAction.Delete && (
        <DeleteConfirmationCheckModal
          onConfirm={() => onDeleteRoleToggle(true)}
          message={t('roles.delete-confirm')}
          onClose={onClose}
          id={role.id}
          onCheckItemUsageBeforeDelete={checkRoleUsageBeforeDelete}
          itemUsagesErrorTitle={t('roles.usages-modal.title')}
          itemUsagesErrorDescription={t('roles.usages-modal.description')}
        />
      )}
      {action === RoleAction.Restore && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onDeleteRoleToggle(false)}
          message={t('roles.restore-confirm')}
        />
      )}
      {action === RoleAction.Disable && (
        <DeleteConfirmationCheckModal
          onConfirm={() => onDisableRoleToggle(true)}
          message={t('roles.deactivate-confirm')}
          onClose={onClose}
          id={role.id}
          onCheckItemUsageBeforeDelete={checkRoleUsageBeforeDelete}
          itemUsagesErrorTitle={t('roles.usages-modal.title')}
          itemUsagesErrorDescription={t('roles.usages-modal.description')}
        />
      )}
      {action === RoleAction.Enable && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onDisableRoleToggle(false)}
          message={t('wttp.question.activate-confirm')}
        />
      )}
    </>
  );
};
